import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

type MoraleState = 'Mutinous' | 'Resentful' | 'Unnerved' | 'Discouraged' | 'Normal' | 'Heartened';
type CleanlinessState = 'Hazardous' | 'Neglected' | 'Standard' | 'Pristine';
type NavigationState = 'On course' | 'Off course' | 'Lost at sea';

interface ManualStatusSetterProps {
  onUpdateMorale: (morale: MoraleState) => void;
  onUpdateCleanliness: (cleanliness: CleanlinessState) => void;
  onUpdateNavigation: (navigation: NavigationState) => void;
  currentMorale: MoraleState;
  currentCleanliness: CleanlinessState;
  currentNavigation: NavigationState;
}

const moraleStates: MoraleState[] = ['Mutinous', 'Resentful', 'Unnerved', 'Discouraged', 'Normal', 'Heartened'];
const cleanlinessStates: CleanlinessState[] = ['Hazardous', 'Neglected', 'Standard', 'Pristine'];
const navigationStates: NavigationState[] = ['On course', 'Off course', 'Lost at sea'];

const ManualStatusSetter: React.FC<ManualStatusSetterProps> = ({
  onUpdateMorale,
  onUpdateCleanliness,
  onUpdateNavigation,
  currentMorale,
  currentCleanliness,
  currentNavigation
}) => {
  const [morale, setMorale] = useState<MoraleState>(currentMorale);
  const [cleanliness, setCleanliness] = useState<CleanlinessState>(currentCleanliness);
  const [navigation, setNavigation] = useState<NavigationState>(currentNavigation);

  const handleUpdateMorale = () => {
    onUpdateMorale(morale);
  };

  const handleUpdateCleanliness = () => {
    onUpdateCleanliness(cleanliness);
  };

  const handleUpdateNavigation = () => {
    onUpdateNavigation(navigation);
  };

  return (
    <Card className="w-full max-w-md mx-auto mt-4">
      <CardHeader>
        <CardTitle>Manual Status Setter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Morale</label>
            <div className="flex space-x-2">
              <Select value={morale} onValueChange={(value: MoraleState) => setMorale(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select morale state" />
                </SelectTrigger>
                <SelectContent>
                  {moraleStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleUpdateMorale}>Update Morale</Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cleanliness</label>
            <div className="flex space-x-2">
              <Select value={cleanliness} onValueChange={(value: CleanlinessState) => setCleanliness(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select cleanliness state" />
                </SelectTrigger>
                <SelectContent>
                  {cleanlinessStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleUpdateCleanliness}>Update Cleanliness</Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Navigation</label>
            <div className="flex space-x-2">
              <Select value={navigation} onValueChange={(value: NavigationState) => setNavigation(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select navigation state" />
                </SelectTrigger>
                <SelectContent>
                  {navigationStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleUpdateNavigation}>Update Navigation</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ManualStatusSetter;